import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useAppScale } from '../../../scaleInterface'
import type { ReplayEvent, ReplayEventType } from '../../../packetsReplay/extractReplayEvents'
import { RepeatIcon } from '../../settings/icons'
import { indexAtTime, packetTimeAt } from '../../state/packetsReplayState'
import styles from './PlayerBottomBar.module.css'
import CollapseTab from './CollapseTab'
import Tooltip, { type TooltipData } from './Tooltip'
import {
  PlayIcon, PauseIcon, SkipBack10Icon, SkipForward10Icon, ChevronDownIcon,
  DeathIcon, KillMobIcon, KillPlayerIcon, ChatMarkerIcon, CommandIcon, TeleportIcon, DimensionIcon
} from './PlayerIcons'

interface Props {
  isPlaying: boolean
  progress: { current: number; total: number }
  speed: number
  totalTimeMs?: number
  replayEvents?: ReplayEvent[]
  seekTo: number | null
  onPlayPause: (playing: boolean) => void
  onSpeedChange: (speed: number) => void
  onSeek: (index: number) => void
}

const formatTime = (ms: number) => {
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

const SPEED_STEPS = [0.25, 0.5, 1, 2, 4, 8]

const EVENT_ICON_SVG: Partial<Record<ReplayEventType, (props: { size?: number }) => JSX.Element>> = {
  death: DeathIcon,
  kill_player: KillPlayerIcon,
  kill_mob: KillMobIcon,
  chat: ChatMarkerIcon,
  command: CommandIcon,
  teleport: TeleportIcon,
  dimension_change: DimensionIcon,
  respawn: RepeatIcon,
}

const ICONLESS_EVENTS = new Set<ReplayEventType>(['low_activity', 'high_activity', 'damage', 'afk'])

const EVENT_CLASS: Partial<Record<ReplayEventType, string>> = {
  damage: styles.damage,
  death: styles.death,
  kill_player: styles.killPlayer,
  kill_mob: styles.mob,
  chat: styles.chat,
  command: styles.command,
  dimension_change: styles.dimc,
  respawn: styles.dim,
  teleport: styles.tp,
  high_activity: styles.highActivity,
}

// ── Event icon stacking ─────────────────────────────────────────────────────
// Overlapping icons pile up left to right in queue order, each on top of the previous
const STACK_STEP_PX = 4

// gap in HUD units between the game HUD (hotbar/xp/bars) and this bar
const HUD_GAP_PX = 12

// height the collapse tab sticks out above the bar when it is collapsed
const COLLAPSED_TAB_PX = 36

// top of the game HUD above the bar, in HUD units: the armor/breath row sits at
// `--safe-area-inset-bottom + 45px` (ArmorBar.css, BreathBar.css) and is 9 tall
const HUD_ROWS_PX = 54

type PlacedEvent = { event: ReplayEvent; x: number }

const EVENT_LABEL: Record<ReplayEventType, string> = {
  death: 'Смерть',
  damage: 'Урон',
  respawn: 'Возрождение',
  dimension_change: 'Смена измерения',
  chat: 'Чат',
  command: 'Команда',
  afk: 'AFK',
  teleport: 'Телепорт',
  kill_player: 'Убийство игрока',
  kill_mob: 'Убийство моба',
  low_activity: 'Малая активность',
  high_activity: 'Активность',
}

// Must mirror .eventIcon:hover — the rect is measured before the transform lands.
const ICON_HOVER_LIFT_PX = 7
const ICON_HOVER_SCALE = 1.4

// How close to an event the cursor has to be on the track to light it up (half an icon)
const HOVER_SNAP_PX = 10

/** `centerX` / `top` describe the icon at rest — the hover transform is added here. */
function eventTooltip (event: ReplayEvent, centerX: number, top: number, height: number): TooltipData {
  const label = EVENT_LABEL[event.type]
  return {
    title: `${formatTime(event.time)} ${label}`,
    description: event.label && event.label !== label ? event.label : null,
    x: centerX,
    y: top - ICON_HOVER_LIFT_PX - (height * (ICON_HOVER_SCALE - 1)) / 2,
  }
}

function renderEventIcon (type: ReplayEventType, size: number) {
  const Svg = EVENT_ICON_SVG[type]
  return Svg ? <Svg size={size} /> : '●'
}

function layoutEvents (events: ReplayEvent[], totalTimeMs: number, widthPx: number): PlacedEvent[] {
  if (!events.length || !totalTimeMs || !widthPx) return []
  let prevX = -Infinity
  return events
    .filter(e => !ICONLESS_EVENTS.has(e.type))
    .sort((a, b) => a.time - b.time)
    .map(event => {
      const x = Math.max((event.time / totalTimeMs) * widthPx, prevX + STACK_STEP_PX)
      prevX = x
      return { event, x }
    })
}

type SegmentType =
  | 'fill' | 'block' | 'blockHot' | 'low' | 'afk'
  | 'evRed' | 'evDamage' | 'evMob' | 'evBlue' | 'evSteel' | 'evCmd'
  | 'evServer' | 'evTp' | 'evDim' | 'evGrey'
type Segment = { left: number; width: number; type: SegmentType }

const EVENT_TICK: Partial<Record<ReplayEventType, { type: SegmentType; w: number }>> = {
  death: { type: 'evRed', w: 0.7 },
  kill_player: { type: 'evBlue', w: 0.42 },
  damage: { type: 'evDamage', w: 0.14 },
  kill_mob: { type: 'evMob', w: 0.21 },
  chat: { type: 'evSteel', w: 0.14 },
  command: { type: 'evCmd', w: 0.14 },
  teleport: { type: 'evTp', w: 0.21 },
  dimension_change: { type: 'evDim', w: 0.28 },
  respawn: { type: 'evDim', w: 0.14 },
}

const BUCKETS = 180

function buildSegments (events: ReplayEvent[], totalTimeMs: number, currentMs: number): Segment[] {
  const segs: Segment[] = []
  if (totalTimeMs <= 0) return segs

  const bucketW = totalTimeMs / BUCKETS
  const active = Array.from<boolean>({ length: BUCKETS }).fill(false)
  const hot = Array.from<boolean>({ length: BUCKETS }).fill(false)
  for (const ev of events) {
    if (ev.type === 'low_activity') continue
    const b = Math.min(BUCKETS - 1, Math.floor(ev.time / bucketW))
    active[b] = true
    if (ev.type === 'high_activity') hot[b] = true
  }

  const pushRuns = (flags: boolean[], type: SegmentType) => {
    let start = -1
    for (let i = 0; i <= BUCKETS; i++) {
      if (i < BUCKETS && flags[i]) { if (start < 0) start = i; continue }
      if (start < 0) continue
      segs.push({ left: (start / BUCKETS) * 100, width: ((i - start) / BUCKETS) * 100, type })
      start = -1
    }
  }
  pushRuns(active, 'block')
  pushRuns(hot, 'blockHot')

  for (const ev of events) {
    if (ev.type !== 'low_activity' || !ev.duration) continue
    segs.push({ left: (ev.time / totalTimeMs) * 100, width: (ev.duration / totalTimeMs) * 100, type: 'low' })
  }

  segs.push({ left: 0, width: (currentMs / totalTimeMs) * 100, type: 'fill' })

  for (const ev of events) {
    const tick = EVENT_TICK[ev.type]
    if (!tick) continue
    segs.push({ left: (ev.time / totalTimeMs) * 100, width: tick.w, type: tick.type })
  }
  return segs
}

export default function PlayerBottomBar ({
  isPlaying, progress, speed, totalTimeMs, replayEvents, seekTo,
  onPlayPause, onSpeedChange, onSeek
}: Props) {
  // Rewinds go through `internalRewindTo` and only become `seekTo` once the
  // engine picks them up, so the readout would lag behind the finger. Hold the
  // dragged position locally and let it win while the drag is on.
  const [scrubIndex, setScrubIndex] = useState<number | null>(null)
  const scrubbing = scrubIndex !== null
  const displayCurrent = scrubIndex ?? (typeof seekTo === 'number' ? seekTo : progress.current)
  // Event marks sit at their real `packet.time`, so the playhead has to as well:
  // deriving the time from the packet index assumes an even packet rate and
  // drifts by seconds on real recordings.
  const currentMs = packetTimeAt(displayCurrent)
    ?? (totalTimeMs && progress.total > 0 ? (displayCurrent / progress.total) * totalTimeMs : 0)
  const headPct = totalTimeMs
    ? Math.max(0, Math.min(100, (currentMs / totalTimeMs) * 100))
    : (progress.total > 0 ? (displayCurrent / progress.total) * 100 : 0)

  // Pointer events rather than mouse ones: the same handler then scrubs under a
  // finger, and pointer capture keeps the drag alive outside the 8px track.
  const rulerRef = useRef<HTMLDivElement>(null)
  const seekToClientX = useCallback((clientX: number) => {
    const rect = rulerRef.current?.getBoundingClientRect()
    if (!rect?.width) return
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const index = (totalTimeMs ? indexAtTime(ratio * totalTimeMs) : null)
      ?? Math.round(ratio * progress.total)
    setScrubIndex(index)
    onSeek(index)
  }, [progress.total, totalTimeMs, onSeek])

  // Shared by the track and by the 22px grab area around the playhead.
  const scrubHandlers = {
    onPointerDown (e: React.PointerEvent<HTMLDivElement>) {
      e.currentTarget.setPointerCapture(e.pointerId)
      seekToClientX(e.clientX)
    },
    onPointerMove (e: React.PointerEvent<HTMLDivElement>) {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) seekToClientX(e.clientX)
    },
    onPointerUp (e: React.PointerEvent<HTMLDivElement>) {
      e.currentTarget.releasePointerCapture(e.pointerId)
      setScrubIndex(null)
    },
    onLostPointerCapture: () => setScrubIndex(null)
  }

  const [speedOpen, setSpeedOpen] = useState(false)
  const speedRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!speedOpen) return
    const onPointerDown = (e: PointerEvent) => {
      if (!speedRef.current?.contains(e.target as Node)) setSpeedOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSpeedOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [speedOpen])

  const skip = (deltaMs: number) => {
    if (!totalTimeMs || progress.total <= 0) return
    const newMs = Math.max(0, Math.min(totalTimeMs, currentMs + deltaMs))
    onSeek(Math.round((newMs / totalTimeMs) * progress.total))
  }

  const segments = totalTimeMs && replayEvents
    ? buildSegments(replayEvents, totalTimeMs, currentMs)
    : [{ left: 0, width: headPct, type: 'fill' as const }]

  const eventsRowRef = useRef<HTMLDivElement>(null)
  const [eventsRowWidth, setEventsRowWidth] = useState(0)
  const [hoveredEvent, setHoveredEvent] = useState<number | null>(null)
  const [tip, setTip] = useState<TooltipData | null>(null)

  const placedEvents = useMemo(
    () => ((replayEvents && totalTimeMs) ? layoutEvents(replayEvents, totalTimeMs, eventsRowWidth) : []),
    [replayEvents, totalTimeMs, eventsRowWidth]
  )

  // Hovering the track lights up the event under the cursor, same as hovering its icon
  const hoverNearestEvent = useCallback((clientX: number) => {
    const row = eventsRowRef.current
    if (!row) return
    const rowRect = row.getBoundingClientRect()
    let best = -1
    let bestDist = HOVER_SNAP_PX
    for (const [i, { x }] of placedEvents.entries()) {
      const dist = Math.abs(rowRect.left + x - clientX)
      if (dist <= bestDist) { bestDist = dist; best = i }
    }
    const placed = best < 0 ? undefined : placedEvents[best]
    setHoveredEvent(placed ? best : null)
    setTip(placed
      ? eventTooltip(placed.event, rowRect.left + placed.x, rowRect.top, rowRect.height)
      : null)
  }, [placedEvents])

  const hasEvents = Boolean(replayEvents?.length && totalTimeMs && progress.total > 0)

  // Ruler tick of the hovered event, drawn over the track so it can grow past its 8px
  const hotEvent = hoveredEvent === null ? undefined : placedEvents[hoveredEvent]?.event
  const hotTick = hotEvent && totalTimeMs ? EVENT_TICK[hotEvent.type] : undefined
  const hotTickLeft = hotEvent && totalTimeMs ? (hotEvent.time / totalTimeMs) * 100 : 0

  const [collapsed, setCollapsed] = useState(false)

  const barRef = useRef<HTMLDivElement>(null)
  const guiScale = useAppScale()
  useEffect(() => {
    const el = barRef.current
    if (!el) return
    const root = document.documentElement
    const apply = () => {
      const screenPx = el.getBoundingClientRect().height
      // Expanded: HUD_GAP_PX is in HUD units so the gap scales with GUI Scale
      // like the hotbar does. Collapsed there is no bar to keep clear of — only
      // the tab, whose own 4px rim is the gap, so the HUD rides right under it.
      const liftPx = collapsed
        ? screenPx + COLLAPSED_TAB_PX
        : screenPx + HUD_GAP_PX * guiScale
      root.style.setProperty('--player-bar-height', `${screenPx}px`)
      // The hotbar is portaled to document.body, so it reads screen px.
      root.style.setProperty('--hud-bottom-max', `${liftPx}px`)
      // The bars live in #ui-root, scaled by --guiScale, so they read HUD units.
      root.style.setProperty('--safe-area-inset-bottom', `calc(${liftPx}px / var(--guiScale, 1))`)
      // Top of the game HUD in screen px, for the panels outside #ui-root.
      root.style.setProperty('--hud-top-px', `${liftPx + HUD_ROWS_PX * guiScale}px`)
    }
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(el)
    window.addEventListener('resize', apply)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', apply)
      root.style.removeProperty('--player-bar-height')
      root.style.removeProperty('--hud-bottom-max')
      root.style.removeProperty('--safe-area-inset-bottom')
      root.style.removeProperty('--hud-top-px')
    }
  }, [collapsed, guiScale])

  useLayoutEffect(() => {
    const el = eventsRowRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setEventsRowWidth(el.clientWidth))
    observer.observe(el)
    setEventsRowWidth(el.clientWidth)
    return () => observer.disconnect()
  }, [collapsed, hasEvents])

  return (
    <div
      ref={barRef}
      className={`${styles.bottomBar}${collapsed ? ` ${styles.collapsed}` : ''}`}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      data-replay-ui
      data-replay-bottom-bar
    >
      {/* Collapse tab — the only thing left on screen once collapsed */}
      <CollapseTab
        edge="top"
        collapsed={collapsed}
        onToggle={() => { setCollapsed(v => !v); setSpeedOpen(false) }}
        title={collapsed ? 'Показать панель плеера' : 'Скрыть панель плеера'}
      />

      <div className={styles.controlsRow}>
        <div className={styles.controls}>
          <button
            className={styles.playBtn}
            data-playing={isPlaying ? 'true' : 'false'}
            onClick={() => onPlayPause(!isPlaying)}
            title={isPlaying ? 'Пауза' : 'Играть'}
          >
            <span className={styles.playIcon}>
              {isPlaying ? <PauseIcon size="100%" /> : <PlayIcon size="100%" />}
            </span>
          </button>
          <div className={styles.skipGroup}>
            <button className={styles.skipBtn} onClick={() => skip(-10_000)} title="-10с">
              <span className={styles.skipIconWrap}>
                <SkipBack10Icon size="100%" />
              </span>
            </button>
            <button className={styles.skipBtn} onClick={() => skip(10_000)} title="+10с">
              <span className={styles.skipIconWrap}>
                <SkipForward10Icon size="100%" />
              </span>
            </button>
          </div>
        </div>

        {/* Sibling of the transport group, not a child: on mobile the row wraps
            and the readout drops onto a line of its own (badge in the mockup). */}
        <span className={styles.timeDisplay}>
          {totalTimeMs ? `${formatTime(currentMs)} / ${formatTime(totalTimeMs)}` : String(displayCurrent)}
        </span>

        <div className={styles.rightControls}>
          <div className={styles.speedSelect} ref={speedRef}>
            <button
              className={`${styles.speedBtn}${speedOpen ? ` ${styles.speedBtnOpen}` : ''}`}
              onClick={() => setSpeedOpen(o => !o)}
              aria-haspopup="listbox"
              aria-expanded={speedOpen}
              title="Скорость воспроизведения"
            >
              ×{speed}
              <ChevronDownIcon className={styles.speedCaret} />
            </button>
            {speedOpen && (
              <div className={styles.speedMenu} role="listbox">
                {SPEED_STEPS.map(step => (
                  <button
                    key={step}
                    role="option"
                    aria-selected={step === speed}
                    className={`${styles.speedOption}${step === speed ? ` ${styles.speedOptionActive}` : ''}`}
                    onClick={() => {
                      onSpeedChange(step)
                      setSpeedOpen(false)
                    }}
                  >
                    ×{step}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {!collapsed && <div className={[styles.timelineWrap, scrubbing && styles.scrubbing].filter(Boolean).join(' ')}>

        {/* Event icon boxes */}
        {hasEvents && (
          <div className={styles.eventsRow} ref={eventsRowRef}>
            {placedEvents.map(({ event, x }, i) => (
              <div
                key={i}
                className={[
                  styles.eventIcon,
                  EVENT_CLASS[event.type] ?? '',
                  hoveredEvent === i ? styles.eventIconHot : ''
                ].filter(Boolean).join(' ')}
                style={{ left: `${x}px`, zIndex: hoveredEvent === i ? 100 : i + 1 }}
                onClick={() => onSeek(indexAtTime(event.time) ?? Math.round((event.time / totalTimeMs!) * progress.total))}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  setHoveredEvent(i)
                  setTip(eventTooltip(event, rect.left + rect.width / 2, rect.top, rect.height))
                }}
                onMouseLeave={() => {
                  setHoveredEvent(prev => (prev === i ? null : prev))
                  setTip(null)
                }}
              >
                {renderEventIcon(event.type, 18)}
              </div>
            ))}
          </div>
        )}
        {!hasEvents && <div style={{ height: 16 }} />}

        {/* Ruler */}
        <div className={styles.rulerWrap}>
          <div
            ref={rulerRef}
            className={styles.ruler}
            {...scrubHandlers}
            onPointerMove={(e) => {
              scrubHandlers.onPointerMove(e)
              hoverNearestEvent(e.clientX)
            }}
            onPointerLeave={() => {
              setHoveredEvent(null)
              setTip(null)
            }}
          >
            {segments.map((seg, i) => (
              <div
                key={i}
                className={`${styles.rulerSegment} ${styles[seg.type]}`}
                style={{ left: `${seg.left}%`, width: `${seg.width}%` }}
              />
            ))}
          </div>
          {hotTick && (
            <div
              className={`${styles.rulerSegment} ${styles[hotTick.type]} ${styles.rulerTickHot}`}
              style={{ left: `${hotTickLeft}%`, width: `${hotTick.w}%` }}
            />
          )}
        </div>

        {/* Playhead spans the whole timeline block, not just the 8px track (Figma 802:2675) */}
        <div className={styles.playhead} style={{ left: `${headPct}%` }}>
          <span className={styles.playheadTime}>
            {totalTimeMs ? formatTime(currentMs) : String(displayCurrent)}
          </span>
          <div className={styles.playheadGrab} {...scrubHandlers} />
        </div>

      </div>}

      <Tooltip tip={collapsed ? null : tip} />
    </div>
  )
}
